import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { motion, useAnimate } from "framer-motion";

type Card2Spec = {
  id: 1 | 2 | 3 | 4;
  img: string;
  x: number;
  y: number;
  w: number;
  h?: number;
  z: number;
};

const card2Items: Card2Spec[] = [
  {
    id: 1,
    img: "/assets/images/home/card2_1.png",
    x: 44.5,
    y: 134.63,
    w: 172.6699,
    h: 112.646,
    z: 30,
  },
  {
    id: 2,
    img: "/assets/images/home/card2_1.png",
    x: 44.5,
    y: 258.27,
    w: 172.6699,
    h: 112.646,
    z: 30,
  },
  {
    id: 3,
    img: "/assets/images/home/card2_2.png",
    x: 120.5,
    y: 199.27,
    w: 453.7112,
    h: 265.1271,
    z: 10,
  },
  {
    id: 4,
    img: "/assets/images/home/card2_3.png",
    x: 464.5,
    y: 480.63,
    w: 110.2296,
    h: 49.5499,
    z: 20,
  },
];

export default function HomeCard2Sequence() {
  const BASE_W = 635;
  const BASE_H = 604;

  //  wrapper 측정용
  const outerRef = useRef<HTMLDivElement | null>(null);
  const [outerWidth, setOuterWidth] = useState(BASE_W);

  //  내부 캔버스(애니메이션/IO 대상)
  const [scope, animate] = useAnimate<HTMLDivElement>();

  const ranRef = useRef(false);

  //  반응형 scale 계산 (wrapper 폭 측정)
  useLayoutEffect(() => {
    const el = outerRef.current;
    if (!el) return;

    const ro = new ResizeObserver(([entry]) => {
      const w = entry?.contentRect?.width ?? BASE_W;
      setOuterWidth(w);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scale = useMemo(() => Math.min(outerWidth / BASE_W, 1), [outerWidth]);
  const scaledH = useMemo(() => BASE_H * scale, [scale]);

  useEffect(() => {
    let alive = true;

    const el = scope.current;
    if (!el) return;

    const run = async () => {
      if (ranRef.current) return;
      ranRef.current = true;

      const root = scope.current;
      if (!root) return;

      const EASE = [0.16, 1, 0.3, 1] as const;
      const INIT_Y = 8;
      const INIT_SCALE = 0.995;
      const GAP = 0.12;
      const DUR_BIG = 1.35;
      const DUR_SMALL = 1.15;
      const DUR_LAST = 1.05;

      const el1 = root.querySelector(".c2-1") as HTMLElement | null;
      const el2 = root.querySelector(".c2-2") as HTMLElement | null;
      const el3 = root.querySelector(".c2-3") as HTMLElement | null;
      const el4 = root.querySelector(".c2-4") as HTMLElement | null;

      // 초기화
      for (const el of [el1, el2, el3, el4]) {
        if (!el) continue;
        await animate(el, { opacity: 0, y: INIT_Y, scale: INIT_SCALE }, { duration: 0 });
      }

      if (!alive) return;

      // 3 -> 1 -> 2 -> 4
      if (el3) await animate(el3, { opacity: 1, y: 0, scale: 1 }, { duration: DUR_BIG, ease: EASE });
      if (!alive) return;

      if (el1) await animate(el1, { opacity: 1, y: 0, scale: 1 }, { duration: DUR_SMALL, ease: EASE, delay: GAP });
      if (!alive) return;

      if (el2) await animate(el2, { opacity: 1, y: 0, scale: 1 }, { duration: DUR_SMALL, ease: EASE, delay: GAP });
      if (!alive) return;

      if (el4) await animate(el4, { opacity: 1, y: 0, scale: 1 }, { duration: DUR_LAST, ease: EASE, delay: GAP });
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) run();
      },
      {
        threshold: 0,
        rootMargin: "-50% 0px -50% 0px",
      }
    );

    io.observe(el);

    return () => {
      alive = false;
      io.disconnect();
    };
  }, [animate, scope]);

  return (
    <section className="w-full flex items-start">
      {/*  바깥 wrapper: w-full로 줄고, 높이는 scaledH로 맞춤 */}
      <div
        ref={outerRef}
        className="w-full max-w-[623px] rounded-[24px] overflow-hidden"
        style={{
          height: scaledH,
          position: "relative",
          background: "#F6F7FB",
          boxShadow: "0 1.813px 19.07px 0 rgba(48, 75, 109, 0.20)",
        }}>
        <img
          src="/assets/images/home/card_bg.png"
          alt=""
          draggable={false}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover", // 이미지가 꽉 차게
            pointerEvents: "none",
            userSelect: "none",
            zIndex: 0,
          }}
        />
        {/*  내부 캔버스: BASE_W/H 기준 좌표계를 유지하고 스케일만 적용 */}
        <div
          ref={scope}
          style={{
            width: BASE_W,
            height: BASE_H,
            position: "absolute",
            left: 0,
            top: 0,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}>
          {card2Items.map((c) => (
            <motion.img
              key={c.id}
              className={`c2-${c.id}`}
              src={c.img}
              alt=""
              draggable={false}
              initial={{ opacity: 0, y: 14, scale: 0.98 }}
              style={{
                position: "absolute",
                left: c.x,
                top: c.y,
                width: c.w,
                height: c.h ?? "auto",
                objectFit: c.h ? "cover" : "initial",
                userSelect: "none",
                zIndex: c.z,
                borderRadius: 16,
                overflow: "hidden",
                boxShadow:
                  c.id === 3
                    ? "0 1.484px 15.612px 0 rgba(48, 75, 109, 0.20)"
                    : "0 0.545px 5.454px 0 rgba(0, 0, 0, 0.08)",
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
